import React, {useCallback, useEffect, useState} from 'react';
import {view, Icon, Action, SelectField, TextField, Skeleton} from '@sail/ui';
import {cancel} from '@sail/icons/react/Icon';
import {defineMessages, FormattedMessage, useIntl} from 'react-intl';
import {formatCurrencyAmountToMajorUnitsFixed, upper} from 'src/utils/helpers';
import type {
  OpenapiCryptocurrencyPayinsResourceSupportedCurrencyCurrencyNetworkEnum,
  OpenapiCryptocurrencyPayinsResourceSupportedCurrencyAssetCodeEnum,
} from '@stripe-internal/data-v1/schema/types';
import {getStore, setValue, useSelector} from 'src/v2/state/store';
import {
  BaseCard,
  QrCode,
  Text,
  SelectFieldItemWithTooltip,
  Button,
} from 'src/v2/components/common';
import {LabelValueCopyRow} from 'src/v2/components/common/LabelValueCopyRow';
import {RefundAddressSection} from 'src/v2/components/directDeposit/RefundAddressSection';
import {useDirectDeposit} from 'src/v2/components/directDeposit/useDirectDeposit';
import {getNetworkIcon} from 'src/components/Wallet/constants';
import {getCurrencyIcon} from 'src/v2/util/getCurrencyIcon';
import {useDirectDepositPaymentPolling} from 'src/v2/components/directDeposit/useDirectDepositPaymentPolling';
import {useUpdateCustomerEmail} from 'src/v2/components/directDeposit/useUpdateCustomerEmail';
import {handleError} from 'src/v2/components/error/handleError';

const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

type Step = 'email' | 'qrCode';

const messages = defineMessages({
  title: {
    id: 'crypto.payins.v2.direct_deposit.title',
    description: 'Title for direct deposit screen',
    defaultMessage: 'Direct deposit via QR code',
  },
  emailLabel: {
    id: 'crypto.payins.v2.direct_deposit_email.email_label',
    description: 'Label for email address input',
    defaultMessage: 'Email address',
  },
  emailPlaceholder: {
    id: 'crypto.payins.v2.direct_deposit_email.email_placeholder',
    description: 'Placeholder for email address input',
    defaultMessage: 'Enter your email address',
  },
  continueButton: {
    id: 'crypto.payins.v2.direct_deposit_email.continue_button',
    description: 'Continue button text',
    defaultMessage: 'Continue',
  },
  network: {
    id: 'crypto.payins.v2.direct_deposit.network',
    description: 'Label for network selector',
    defaultMessage: 'Network',
  },
  currency: {
    id: 'crypto.payins.v2.direct_deposit.currency',
    description: 'Label for currency selector',
    defaultMessage: 'Currency',
  },
  paymentInstruction: {
    id: 'crypto.payins.v2.direct_deposit.payment_instruction',
    description: 'Payment instruction text',
    defaultMessage: 'Pay {amount} {currency} on {network}',
  },
  warningText: {
    id: 'crypto.payins.v2.direct_deposit.warning_text',
    description: 'Warning about wrong network or currency',
    defaultMessage:
      "Payments sent to the wrong network or currency can't be recovered. Processing may take up to 2 minutes.",
  },
  depositAddress: {
    id: 'crypto.payins.v2.direct_deposit.deposit_address',
    description: 'Label for deposit address',
    defaultMessage: 'Address',
  },
  amount: {
    id: 'crypto.payins.v2.direct_deposit.amount',
    description: 'Label for deposit amount',
    defaultMessage: 'Amount',
  },
});

const EmailStep = ({onContinue}: {onContinue: () => void}) => {
  const intl = useIntl();
  const initialEmail = useSelector(
    (state) => state.directDeposit.customerEmail,
  );
  const [updateCustomerEmail, {loading}] = useUpdateCustomerEmail();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [email, setEmail] = useState(initialEmail || '');

  const trimmedEmail = email.trim();
  const isEmailValid =
    trimmedEmail.length > 0 && EMAIL_REGEX.test(trimmedEmail);
  const isButtonDisabled = !isEmailValid || isSubmitting;

  const handleEmailChange = (newEmail: string) => {
    setEmail(newEmail);
    setValue((state) => {
      state.directDeposit.customerEmail = newEmail;
    });
  };

  const handleContinue = async () => {
    setIsSubmitting(true);
    try {
      await updateCustomerEmail(email);
      onContinue();
    } catch (error) {
      handleError({error, intl, context: 'direct_deposit'});
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <TextField
        label={intl.formatMessage(messages.emailLabel)}
        placeholder={intl.formatMessage(messages.emailPlaceholder)}
        value={email}
        onChange={(e) => handleEmailChange(e.target.value)}
        type="email"
      />

      <Button
        onClick={handleContinue}
        disabled={isButtonDisabled}
        loading={loading || isSubmitting}
      >
        <FormattedMessage {...messages.continueButton} />
      </Button>
    </>
  );
};

const QrCodeStep = () => {
  const intl = useIntl();
  const {
    selectedNetwork,
    setSelectedNetwork,
    selectedCurrency,
    setSelectedCurrency,
    networkOptions,
    currencyOptions,
    depositAddress,
    isAllocating,
  } = useDirectDeposit();

  const {startPolling} = useDirectDepositPaymentPolling('QR_CODE');

  const store = getStore();
  const depositAmount = store.useSelector(
    (state) => state.paymentDetails.amount,
  );
  const depositCurrency = store.useSelector(
    (state) => state.paymentDetails.currency,
  );

  useEffect(() => {
    startPolling();
  }, [startPolling]);

  const amount = formatCurrencyAmountToMajorUnitsFixed(
    depositAmount,
    depositCurrency,
  );

  const handleCopyDepositAddress = useCallback(() => {
    navigator.clipboard.writeText(depositAddress);
  }, [depositAddress]);

  const handleCopyAmount = useCallback(() => {
    navigator.clipboard.writeText(amount);
  }, [amount]);

  return (
    <>
      <view.div css={{display: 'flex', gap: 'small'}}>
        <SelectField
          label={<FormattedMessage {...messages.network} />}
          selectedKey={selectedNetwork}
          onSelectionChange={(key) => {
            const option = networkOptions.find((opt) => opt.network === key);
            if (option && !option.isDisabled) {
              setSelectedNetwork(
                key as OpenapiCryptocurrencyPayinsResourceSupportedCurrencyCurrencyNetworkEnum,
              );
            }
          }}
          size="small"
          css={{flex: '1 1 0', minWidth: 'space.0'}}
        >
          {[...networkOptions]
            .sort((a, b) => Number(a.isDisabled) - Number(b.isDisabled))
            .map(({network, isDisabled, compatibleCurrencies}) => {
              const disabledTooltip =
                isDisabled && compatibleCurrencies
                  ? `${upper(
                      network,
                    )} is only available with ${compatibleCurrencies
                      .map((c) => c.toUpperCase())
                      .join(', ')}`
                  : undefined;

              return (
                <SelectFieldItemWithTooltip
                  key={network}
                  id={network}
                  label={upper(network)}
                  isDisabled={isDisabled}
                  disabledTooltip={disabledTooltip}
                  iconElement={getNetworkIcon(network)}
                />
              );
            })}
        </SelectField>
        <SelectField
          label={<FormattedMessage {...messages.currency} />}
          selectedKey={selectedCurrency}
          onSelectionChange={(key) => {
            const option = currencyOptions.find((opt) => opt.currency === key);
            if (option && !option.isDisabled) {
              setSelectedCurrency(
                key as OpenapiCryptocurrencyPayinsResourceSupportedCurrencyAssetCodeEnum,
              );
            }
          }}
          size="small"
          css={{flex: '1 1 0', minWidth: 'space.0'}}
        >
          {[...currencyOptions]
            .sort((a, b) => Number(a.isDisabled) - Number(b.isDisabled))
            .map(({currency, isDisabled, compatibleNetworks}) => {
              const disabledTooltip =
                isDisabled && compatibleNetworks
                  ? `${currency.toUpperCase()} is only available with ${compatibleNetworks
                      .map((n) => upper(n))
                      .join(', ')}`
                  : undefined;

              return (
                <SelectFieldItemWithTooltip
                  key={currency}
                  id={currency}
                  label={currency.toUpperCase()}
                  isDisabled={isDisabled}
                  disabledTooltip={disabledTooltip}
                  iconElement={getCurrencyIcon(currency)}
                />
              );
            })}
        </SelectField>
      </view.div>

      <view.div
        css={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '24px 12px',
          gap: 'medium',
          background: 'rgba(255, 255, 255, 0.72)',
          border: '1px solid rgba(26, 26, 26, 0.05)',
          borderRadius: 'small',
        }}
      >
        <Text type="bodyEmphasized" css={{textAlign: 'center'}}>
          <FormattedMessage
            {...messages.paymentInstruction}
            values={{
              amount,
              currency: selectedCurrency.toUpperCase(),
              network: upper(selectedNetwork),
            }}
          />
        </Text>

        <view.div
          css={{
            width: '208px',
            height: '207px',
            background: '#FFFFFF',
            borderRadius: 'medium',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 'xsmall',
          }}
        >
          {isAllocating ? (
            <Skeleton css={{width: '200px', height: '200px'}} />
          ) : (
            <QrCode qrCodeUri={depositAddress} size={200} />
          )}
        </view.div>

        <Text type="caption" css={{textAlign: 'center', color: '#707070'}}>
          <FormattedMessage {...messages.warningText} />
        </Text>
      </view.div>

      <view.div
        css={{
          stack: 'y',
          gapY: 'small',
          paddingY: 'small',
        }}
      >
        <LabelValueCopyRow
          label={intl.formatMessage(messages.depositAddress)}
          value={depositAddress}
          onCopy={handleCopyDepositAddress}
          loading={isAllocating}
          contentContainerStyle={{
            paddingRight: 'small',
          }}
        />
        <LabelValueCopyRow
          label={intl.formatMessage(messages.amount)}
          value={amount}
          onCopy={handleCopyAmount}
          contentContainerStyle={{
            paddingRight: 'small',
          }}
        />
      </view.div>
      <RefundAddressSection />
    </>
  );
};

export const DirectDepositScreen = () => {
  const [step, setStep] = useState<Step>('email');

  const handleClose = useCallback(() => {
    setValue((state) => {
      state.screen = 'WALLET_SELECT_GRID';
    });
  }, []);

  const handleContinueToQrCode = useCallback(() => {
    setStep('qrCode');
  }, []);

  const header = (
    <>
      <Text type="bodyEmphasized">
        <FormattedMessage {...messages.title} />
      </Text>

      <Action onPress={handleClose}>
        <view.div css={{cursor: 'pointer', padding: 'xsmall'}}>
          <Icon icon={cancel} size="xsmall" />
        </view.div>
      </Action>
    </>
  );

  return (
    <BaseCard header={header}>
      {step === 'email' ? (
        <EmailStep onContinue={handleContinueToQrCode} />
      ) : (
        <QrCodeStep />
      )}
    </BaseCard>
  );
};
